import React, { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { apiClient } from '../api/client'
import './AuthForm.css'

/**
 * OIDC login form. Redirects to the OIDC provider's authorization endpoint
 * using Authorization Code Flow with PKCE.
 */
const AuthForm: React.FC = () => {
  const { error } = useAuth()
  const [loading, setLoading] = useState(true)
  const [authUrl, setAuthUrl] = useState<string | null>(null)
  const [persistedError, setPersistedError] = useState<string | null>(null)

  useEffect(() => {
    const saved = sessionStorage.getItem('auth_error')
    if (saved) {
      setPersistedError(saved)
      sessionStorage.removeItem('auth_error')
    }
  }, [])

  useEffect(() => {
    buildAuthUrl()
  }, [])

  const buildAuthUrl = async () => {
    try {
      const config = await apiClient.getOidcConfig()
      if (!config.client_id) {
        setLoading(false)
        return
      }

      const redirectUri = `${window.location.origin}/auth/callback`
      const codeVerifier = generateCodeVerifier()
      const codeChallenge = await generateCodeChallenge(codeVerifier)
      const state = generateState()

      // Store PKCE verifier and state for the callback handler
      localStorage.setItem('pkce_code_verifier', codeVerifier)
      localStorage.setItem('pkce_redirect_uri', redirectUri)
      localStorage.setItem('oauth_state', state)

      const params = new URLSearchParams({
        response_type: 'code',
        client_id: config.client_id,
        redirect_uri: redirectUri,
        scope: 'openid profile email',
        state,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
      })

      // Try OIDC discovery for the authorization endpoint
      let authEndpoint: string | null = null
      try {
        const resp = await fetch(`${config.issuer.replace(/\/$/, '')}/.well-known/openid-configuration`)
        if (resp.ok) {
          const doc = await resp.json()
          if (doc.authorization_endpoint) {
            authEndpoint = `${doc.authorization_endpoint}?${params}`
          }
        }
      } catch { /* discovery failed, use fallback */ }

      if (!authEndpoint) {
        const issuer = config.issuer.replace(/\/$/, '')
        authEndpoint = `${issuer}/oauth/authorize?${params}`
      }

      setAuthUrl(authEndpoint)
    } catch {
      // Config fetch failed — show error
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = () => {
    if (authUrl) {
      window.location.href = authUrl
    }
  }

  return (
    <div className="auth-form-container">
      <div className="auth-form-card">
        <div className="auth-header">
          <h2>Manaへようこそ</h2>
          <p>日本語学習の旅を続けるにはサインインしてください</p>
        </div>

        {(error || persistedError) && <div className="error-message">{error || persistedError}</div>}

        <button
          type="button"
          className="auth-submit-btn"
          onClick={handleLogin}
          disabled={loading || !authUrl}
        >
          {loading ? '読み込み中...' : 'SSOでサインイン'}
        </button>

        {!loading && !authUrl && (
          <p className="error-message">認証が設定されていません。</p>
        )}
      </div>
    </div>
  )
}

function generateState(): string {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return base64URLEncode(array)
}

function generateCodeVerifier(): string {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return base64URLEncode(array)
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(verifier)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return base64URLEncode(new Uint8Array(digest))
}

function base64URLEncode(buffer: Uint8Array): string {
  return btoa(String.fromCharCode(...buffer))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

export default AuthForm